import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { UploadCloud, Copy, Check, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';
const UPLOAD_EMAIL = 'orders@c30dashboard.uk';

/** Computes the M4 "financial year end" date the user must set when running the report.
 *  FY ends 31 July. Jan–Jul → 31/07 of this year. Aug–Dec → 31/07 of next year. */
function financialYearEndDate() {
  const now = new Date();
  const year = now.getMonth() <= 6 ? now.getFullYear() : now.getFullYear() + 1; // month 6 = July
  return `31/07/${year}`;
}

/**
 * Upload modal shown from the BI Reports page status pill.
 * Explains exactly how to run the correct M4 report, the date to set, where
 * to send it, and offers a direct drag-and-drop / browse upload as well.
 */
const BIReportUploadModal = ({ isOpen, onClose, reportConfig }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => { setResult(null); setUploading(false); setDragOver(false); onClose(); };

  const copyEmail = () => {
    navigator.clipboard.writeText(UPLOAD_EMAIL);
    setCopied(true);
    toast.success('Email address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`${API}/order-book-reports/smart-upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult({ ok: true, ...res.data });
      toast.success(res.data?.message || 'Report uploaded successfully');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed — file type not recognised';
      setResult({ ok: false, message: detail });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="bi-report-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2" style={{ color: enconOrange }}>
            <UploadCloud className="w-7 h-7" />
            Upload {reportConfig.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-xl border p-3.5 space-y-2" style={{ borderColor: `${enconOrange}55`, background: `${enconOrange}0d` }}>
            <p className="text-gray-700">
              Run this report in M4: <span className="font-semibold" style={{ color: enconOrange }}>{reportConfig.m4ReportName}</span>
            </p>
            <p className="text-gray-700">
              Set the date to <span className="font-semibold" style={{ color: enconOrange }} data-testid="bi-report-fy-date">{financialYearEndDate()}</span> (this financial year's end date)
            </p>
          </div>

          <button
            onClick={copyEmail}
            data-testid="bi-report-copy-email"
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border text-sm transition-colors hover:bg-orange-50"
            style={{ borderColor: '#d1d5db' }}
          >
            <span className="text-gray-600 text-left">Send it to your own @encon.co.uk email, then forward that to:</span>
            <span className="flex items-center gap-1.5 font-semibold whitespace-nowrap" style={{ color: enconOrange }}>
              {UPLOAD_EMAIL}
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
            </span>
          </button>

          <p className="text-center text-xs text-gray-400">— or upload the file directly below —</p>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            data-testid="bi-report-dropzone"
            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors"
            style={{ borderColor: dragOver ? enconOrange : '#d1d5db', background: dragOver ? `${enconOrange}0d` : '#fafafa' }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
              data-testid="bi-report-file-input"
            />
            {uploading ? (
              <>
                <Loader2 className="w-7 h-7 mb-2 animate-spin" style={{ color: enconOrange }} />
                <p className="text-xs text-gray-500">Uploading…</p>
              </>
            ) : (
              <>
                <UploadCloud className="w-7 h-7 mb-2" style={{ color: enconOrange }} />
                <p className="text-xs text-gray-600"><span className="font-semibold">Click to browse</span> or drag &amp; drop</p>
              </>
            )}
          </div>

          {result && (
            <div data-testid="bi-report-upload-result" className={`flex items-start gap-2 p-3 rounded-xl border text-xs ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              {result.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
              <p className={result.ok ? 'text-green-700' : 'text-red-600'}>{result.message}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BIReportUploadModal;
